import { Api, ApiError } from '@webapp/util/Api'
import { AccessTokenResponse } from './AccessTokenResponse'
import { Environment } from '@webapp/util/Environment'
import { DateTime } from 'luxon'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { toBase64Url, toHashStringAsync } from '@webapp/util/SecurityUtil'

const devicePath = 'oauth2/device'
const tokenPath = 'oauth2/token'

export class LocalTokenApi {

    static async getLocalAccessTokenAsync(identityToken: string): Promise<AccessTokenResponse> {
        return await exchangeTokenAsync(identityToken, Environment.get('LOCAL_TOKEN_AUDIENCE'), "User")
    }

    static async getDeviceCodeAsync(clientId: string, codeVerifier: string) {
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => '')
        const codeChallenge = toBase64Url(await toHashStringAsync(codeVerifier))
        const request = {
            client_id: clientId,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
        }
        return await api.postPlainAsync(devicePath + '/code', request, {}, true) as DeviceCodeResponse
    }

    static async approveDeviceCodeAsync(clientId: string, approvalToken: string, accessToken: string) {
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => accessToken)
        const request = { verification_token: approvalToken }
        await api.postPlainAsync(devicePath + '/approve', request, {}, true)
    }

    /**
     * Polls the token endpoint for device code authorization.
     * Returns null if the user hasn't authorized yet (authorization_pending).
     */
    static async getLocalAccessTokenFromDeviceCodeAsync(clientId: string, deviceCode: string, codeVerifier: string) {
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => '')
        const body = {
            grant_type: 'device_code',
            device_code: deviceCode,
            client_id: clientId,
            code_verifier: codeVerifier,
        }
        const result = await api.postRawAsync(tokenPath, body, {}, true)
        if (result.ok)
            return result.json<AccessTokenResponse>()
        if (result.status === 400 && result.data) {
            try {
                if (JSON.parse(result.data)?.error === 'authorization_pending')
                    return null
            } catch { /* not JSON — fall through to throw */ }
        }
        throw new ApiError(result.status, result.statusText, result.data)
    }

    static async getLocalAccessTokenFromAuthCodeAsync(clientId: string, authCode: string) {
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => '');
        const request = {
            grant_type: "authorization_code",
            requested_token_type: "access_token",
            client_id: clientId,
            code: authCode,
        };
        return await api.postPlainAsync(tokenPath, request, {}, true) as AccessTokenResponse;
    }

    static async getLocalIdentityTokenAsync(localAccessToken: string) {
        const audience = Environment.get('IDENTITY_URL').slice(0, -1) // remove trailing slash
        return await exchangeTokenAsync(localAccessToken, audience, "User")
    }

    static async getLocalAccessTokenForUserAsync(subjectToken: string, forUserId: string) {
        const audience = Environment.get('LOCAL_TOKEN_AUDIENCE')
        return await exchangeTokenAsync(subjectToken, audience, `User.${forUserId}`)
    }

    static async refreshAccessToken(accessToken: string, refreshToken: string) {
        // use current access token (expired okay) to authenticate the request
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => accessToken)
        const request = {
            grant_type: "refresh_token",
            refresh_token: refreshToken,
        }
        return await api.postPlainAsync(tokenPath, request, {}, true) as AccessTokenResponse
    }

    static async getTokenClientAsync(accessToken: string, clientId: string) {
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => accessToken)
        return await api.getPlainAsync(`oauth2/clients/${clientId}`) as TokenClient
    }

    static async getLocalAuthCodeAsync(accessToken: string, clientId: string, pkce?: PkceOptions) {
        const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => accessToken)
        const query = {
            client_id: clientId,
            redirect_uri: pkce?.redirectUri,
            code_challenge: pkce?.codeChallenge,
            code_challenge_method: pkce?.codeChallengeMethod,
        }
        const p = await api.postPlainAsync('oauth2/authorize', undefined, query)
        p.expirationDate = DateTimeUtil.fromAPI(p.expirationDate)
        return p as AuthCodeResponse
    }
}

export async function exchangeTokenAsync(subjectToken: string, audience: string, scope: string) {
    // current access token is specified in the body, not in the headers
    const api = new Api(Environment.get('GROUP_ADMIN_URL'), () => '');
    const request = {
        grant_type: "token-exchange",
        subject_token_type: "access_token",
        subject_token: subjectToken,
        requested_token_type: "access_token",
        audience,
        scope,
    };
    return await api.postPlainAsync(tokenPath, request, {}, true) as AccessTokenResponse;
}

export interface DeviceCodeResponse {
    device_code: string
    verification_token: string
    expires_in: number
    interval: number
}

export interface AuthCodeResponse {
    code: string
    expirationDate: DateTime
    notificationId: string
}

export interface TokenClient {
    name: string
}

export const tokenClientIcons = new Map([
    ['Kv3nRw8xJd', new URL("@/assets/icons/flask_gray_150.png", import.meta.url).href],
])

export interface PkceOptions {
    redirectUri: string
    codeChallenge: string
    codeChallengeMethod: string
}
